import React, { useState, useEffect } from 'react';
import Header from '../components/ui/Header';
import Footer from '../components/Footer';
import SEO from '../components/SEO';
import FAQ from '../components/ui/FAQ';
import { contactFAQs } from '../data/faqs';
import Icon from '../components/AppIcon';
import { sendContactFormEmail } from '../utils/emailService';
import { useContactInfo, getWhatsAppUrl } from '../utils/contactObfuscation';

const ContactUs = () => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    subject: '',
    message: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitMessage, setSubmitMessage] = useState('');
  const obfuscatedContact = useContactInfo();
  
  // Enhanced SEO Data
  const seoData = {
    title: 'Contact Us - OctalChip | Get in Touch for Web & Mobile Development',
    description: 'Contact OctalChip for web development, mobile app development, AI solutions, and digital transformation services. Get a free consultation and quote for your project.',
    keywords: 'contact OctalChip, web development contact, mobile app development contact, software development contact, development agency contact, tech consulting contact, IT services contact, project consultation, free quote, development services, custom software development, web applications, mobile applications, AI development, cloud services, DevOps, UI/UX design, enterprise software, SaaS development, e-commerce development, API development, database design, frontend development, backend development, full-stack development, project inquiry, development quote, technology consultation',
    canonical: 'https://www.octalchip.com/contact',
    tags: ['contact OctalChip', 'project consultation', 'free quote', 'development services'],
    additionalMeta: [
      { name: 'theme-color', content: '#00d690' },
      { name: 'mobile-web-app-capable', content: 'yes' }
    ],
    breadcrumbs: [
      { name: 'Home', url: 'https://www.octalchip.com/' },
      { name: 'Contact Us', url: 'https://www.octalchip.com/contact' }
    ],

    // GEO-specific data
    geoData: {
      region: "IN-GJ",
      placename: "Gujarat, India",
      latitude: "23.0225",
      longitude: "72.5714"
    },
    localBusinessData: true,
    serviceArea: [
      { "@type": "Country", "name": "United States" },
      { "@type": "Country", "name": "United Kingdom" },
      { "@type": "Country", "name": "Germany" },
      { "@type": "Country", "name": "France" },
      { "@type": "Country", "name": "Netherlands" },
      { "@type": "Country", "name": "Canada" },
      { "@type": "Country", "name": "Australia" },
      { "@type": "Country", "name": "UAE" },
      { "@type": "Country", "name": "Singapore" },
      { "@type": "Country", "name": "India" },
      { "@type": "Country", "name": "Saudi Arabia" },
      { "@type": "Country", "name": "Mexico" },
      { "@type": "Country", "name": "Brazil" },
      { "@type": "Country", "name": "Japan" },
      { "@type": "Country", "name": "South Korea" },
      { "@type": "Country", "name": "Poland" },
      { "@type": "Country", "name": "Romania" },
      { "@type": "Country", "name": "Bulgaria" }
    ],

    // AEO-specific data
    faqData: contactFAQs,

    // Voice search optimization
    voiceSearchKeywords: [
      "contact OctalChip",
      "web development contact",
      "mobile app development contact",
      "software development consultation",
      "development company contact",
      "tech consulting contact",
      "project inquiry",
      "free consultation"
    ],
    conversationalQueries: [
      "How can I contact OctalChip?",
      "How to get a quote from OctalChip?",
      "What are OctalChip's business hours?",
      "How to schedule a consultation with OctalChip?"
    ],

    // Featured snippet optimization
    featuredSnippetContent: "Contact OctalChip for web development, mobile app development, and AI solutions. Email: business@octalchip.com. Free 30-minute consultations available.",
    
    // List data for featured snippets
    listData: {
      name: "OctalChip Contact Methods",
      description: "Different ways to contact OctalChip for your project needs",
      items: [
        {
          name: "Email Contact",
          description: "Send detailed project requirements via email",
          url: "mailto:business@octalchip.com"
        },
        {
          name: "Phone Call",
          description: "Call for immediate assistance during business hours",
          url: "#contact"
        },
        {
          name: "WhatsApp Chat",
          description: "Quick responses via WhatsApp messaging",
          url: "#contact"
        },
        {
          name: "Contact Form",
          description: "Fill out our detailed contact form online",
          url: "https://www.octalchip.com/contact"
        },
        {
          name: "Schedule Meeting",
          description: "Book a 30-minute free consultation",
          url: "https://cal.com/octalchip/30min"
        }
      ]
    },
    structuredData: {
      "@context": "https://schema.org",
      "@type": "ContactPage",
      "name": "Contact OctalChip",
      "description": "Contact OctalChip for web development, mobile app development, AI solutions, and digital transformation services.",
      "url": "https://www.octalchip.com/contact",
      "mainEntity": {
        "@type": "Organization",
        "name": "OctalChip",
        "url": "https://www.octalchip.com",
        "logo": "https://www.octalchip.com/favicon.svg",
        "description": "Leading web and mobile app development company offering AI-powered solutions, custom software development, cloud services, and digital transformation.",
        "contactPoint": [
          {
            "@type": "ContactPoint",
            "contactType": "customer service",
            "email": "business@octalchip.com",
            "availableLanguage": "English",
            "hoursAvailable": {
              "@type": "OpeningHoursSpecification",
              "dayOfWeek": ["Monday", "Tuesday", "Wednesday", "Thursday", "Friday"],
              "opens": "09:00",
              "closes": "18:00"
            }
          },
          {
            "@type": "ContactPoint",
            "contactType": "sales",
            "email": "business@octalchip.com",
            "availableLanguage": "English"
          },
          {
            "@type": "ContactPoint",
            "contactType": "technical support",
            "email": "business@octalchip.com",
            "availableLanguage": "English"
          }
        ],
        "address": {
          "@type": "PostalAddress",
          "streetAddress": "404, Opera point, kothariya",
          "addressLocality": "Gujarat",
          "addressCountry": "IN",
          "postalCode": "360022"
        },
        "sameAs": [
          "https://linkedin.com/company/octalchip",
          "https://twitter.com/octalchip",
          "https://github.com/octalchip",
          "https://www.facebook.com/octalchip",
          "https://www.instagram.com/octalchiptech/",
          "https://discord.gg/sNbNevBZ"
        ]
      }
    }
  };

  const handleInputChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    setSubmitMessage('');

    try {
      await sendContactFormEmail(formData);
      setSubmitMessage('Thank you! Your message has been sent successfully. We\'ll get back to you soon.');
      setFormData({
        name: '',
        email: '',
        phone: '',
        subject: '',
        message: ''
      });
    } catch (error) {
      setSubmitMessage('Sorry, there was an error sending your message. Please try again or contact us directly.');
    } finally {
      setIsSubmitting(false);
    }
  };

  const contactInfoList = [
    {
      icon: 'MapPin',
      title: 'Head Office',
      details: [
        'Octalchip',
        '404, Opera point, kothariya,',
        'gujarat, india, 360022'
      ],
      link: 'https://maps.google.com/?q=404+Opera+point+kothariya+gujarat+india+360022'
    },
    {
      icon: 'Mail',
      title: 'Email',
      details: ['business@octalchip.com'],
      link: 'mailto:business@octalchip.com'
    },
    {
      icon: 'Phone',
      title: 'Phone',
      details: obfuscatedContact.phone ? [obfuscatedContact.phone] : [],
      link: obfuscatedContact.phoneLink || null
    },
    {
      icon: 'Clock',
      title: 'Working Hours',
      details: ['Mon - Fri, 9AM - 6PM'],
      link: null
    }
  ];

  const socialLinks = [
    { name: 'Facebook', icon: 'Facebook', url: 'https://www.facebook.com/octalchip' },
    { name: 'Instagram', icon: 'Instagram', url: 'https://www.instagram.com/octalchiptech/' },
    { name: 'LinkedIn', icon: 'Linkedin', url: 'https://www.linkedin.com/company/octalchip' },
    { name: 'Twitter', icon: 'Twitter', url: 'https://x.com/octalchip' },
    { name: 'Discord', icon: 'Discord', url: 'https://discord.gg/sNbNevBZ' }
  ];

  return (
    <div className="min-h-screen bg-[#f4faf8]">
      <Header />
      <SEO 
        title={seoData.title}
        description={seoData.description}
        keywords={seoData.keywords}
        canonical={seoData.canonical}
        ogImage={seoData.ogImage}
        tags={seoData.tags}
        additionalMeta={seoData.additionalMeta}
        breadcrumbs={seoData.breadcrumbs}
        faqData={seoData.faqData}
        structuredData={seoData.structuredData}
        geoData={seoData.geoData}
        localBusinessData={seoData.localBusinessData}
        serviceArea={seoData.serviceArea}
        voiceSearchKeywords={seoData.voiceSearchKeywords}
        conversationalQueries={seoData.conversationalQueries}
        featuredSnippetContent={seoData.featuredSnippetContent}
        listData={seoData.listData}
      />
      
      {/* Hero Section */}
      <section id="hero" className="bg-white py-8 sm:py-12 md:py-16 lg:py-20">
        <div className="container mx-auto px-4 sm:px-6 md:px-8">
          <div className="text-center max-w-4xl mx-auto">
            <h1 className="text-2xl sm:text-3xl md:text-4xl lg:text-5xl font-bold text-gray-800 mb-4 sm:mb-6">
              Get In Touch
            </h1>
            <div className="w-16 sm:w-20 md:w-24 h-1 bg-[#00d690] mx-auto mb-6 sm:mb-8"></div>
            <p className="text-sm sm:text-base md:text-lg lg:text-xl text-gray-600 leading-relaxed px-4">
              Ready to start your next project? We'd love to hear from you. 
              Let's discuss how we can help bring your ideas to life.
            </p>
          </div>
        </div>
      </section>

      {/* Contact Information Section */}
      <section id="contact-information" className="py-8 sm:py-12 md:py-16 lg:py-20 bg-gray-50">
        <div className="container mx-auto px-4 sm:px-6 md:px-8">
          <div className="text-center mb-8 sm:mb-12 md:mb-16">
            <h2 className="text-2xl sm:text-3xl md:text-4xl font-bold text-gray-800 mb-3 sm:mb-4">Contact Information</h2>
            <div className="w-16 sm:w-20 md:w-24 h-1 bg-[#00d690] mx-auto"></div>
          </div>
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-4 sm:gap-6 md:gap-8">
            {contactInfoList.map((info, index) => (
              <div key={index} className="text-center p-6 bg-white rounded-lg shadow-lg hover:shadow-xl transition-shadow">
                <div className="w-16 h-16 bg-[#00d690]/10 rounded-full flex items-center justify-center mx-auto mb-4">
                  <Icon name={info.icon} size={32} className="text-[#00d690]" />
                </div>
                <h3 className="text-xl font-bold text-gray-800 mb-3">{info.title}</h3>
                {info.link ? (
                  <a 
                    href={info.link} 
                    target={info.link.startsWith('http') ? '_blank' : '_self'}
                    rel={info.link.startsWith('http') ? 'noopener noreferrer' : ''}
                    className="text-gray-600 hover:text-[#00d690] transition-colors"
                  >
                    {info.details.map((detail, idx) => (
                      <p key={idx} className="mb-1">{detail}</p>
                    ))}
                  </a>
                ) : (
                  info.details.map((detail, idx) => (
                    <p key={idx} className="text-gray-600 mb-1">{detail}</p>
                  ))
                )}
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Contact Form Section */}
      <section id="contact-form" className="py-8 sm:py-12 md:py-16 lg:py-20 bg-white">
        <div className="container mx-auto px-4 sm:px-6 md:px-8">
          <div className="max-w-4xl mx-auto">
            <div className="text-center mb-8 sm:mb-12 md:mb-16">
              <h2 className="text-2xl sm:text-3xl md:text-4xl font-bold text-gray-800 mb-3 sm:mb-4">Send Us a Message</h2>
              <div className="w-16 sm:w-20 md:w-24 h-1 bg-[#00d690] mx-auto"></div>
            </div>
            
            <div className="grid md:grid-cols-2 gap-6 sm:gap-8 md:gap-12">
              {/* Contact Form */}
              <div>
                <form onSubmit={handleSubmit} className="space-y-6">
                  <div className="grid md:grid-cols-2 gap-6">
                    <div>
                      <label htmlFor="name" className="block text-sm font-medium text-gray-700 mb-2">
                        Full Name *
                      </label>
                      <input
                        type="text"
                        id="name"
                        name="name"
                        value={formData.name}
                        onChange={handleInputChange}
                        required
                        className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#00d690]/20 focus:border-[#00d690]"
                        placeholder="Enter your full name"
                      />
                    </div>
                    <div>
                      <label htmlFor="email" className="block text-sm font-medium text-gray-700 mb-2">
                        Email Address *
                      </label>
                      <input
                        type="email"
                        id="email"
                        name="email"
                        value={formData.email}
                        onChange={handleInputChange}
                        required
                        className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#00d690]/20 focus:border-[#00d690]"
                        placeholder="Enter your email"
                      />
                    </div>
                  </div>
                  
                  <div className="grid md:grid-cols-2 gap-6">
                    <div>
                      <label htmlFor="phone" className="block text-sm font-medium text-gray-700 mb-2">
                        Phone Number
                      </label>
                      <input
                        type="tel"
                        id="phone"
                        name="phone"
                        value={formData.phone}
                        onChange={handleInputChange}
                        className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#00d690]/20 focus:border-[#00d690]"
                        placeholder="Enter your phone number"
                      />
                    </div>
                    <div>
                      <label htmlFor="subject" className="block text-sm font-medium text-gray-700 mb-2">
                        Subject *
                      </label>
                      <input
                        type="text"
                        id="subject"
                        name="subject"
                        value={formData.subject}
                        onChange={handleInputChange}
                        required
                        className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#00d690]/20 focus:border-[#00d690]"
                        placeholder="What is this about?"
                      />
                    </div>
                  </div>
                  
                  <div>
                    <label htmlFor="message" className="block text-sm font-medium text-gray-700 mb-2">
                      Message *
                    </label>
                    <textarea
                      id="message"
                      name="message"
                      value={formData.message}
                      onChange={handleInputChange}
                      required
                      rows={6}
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#00d690]/20 focus:border-[#00d690]"
                      placeholder="Tell us about your project or inquiry..."
                    ></textarea>
                  </div>
                  
                  {submitMessage && (
                    <div className={`p-4 rounded-lg ${
                      submitMessage.includes('Thank you') 
                        ? 'bg-green-50 text-green-800 border border-green-200' 
                        : 'bg-red-50 text-red-800 border border-red-200'
                    }`}>
                      {submitMessage}
                    </div>
                  )}
                  
                  <button
                    type="submit"
                    disabled={isSubmitting}
                    className="w-full bg-[#00d690] hover:bg-[#00b876] text-white py-2.5 sm:py-3 px-4 sm:px-6 rounded-lg font-semibold transition-colors disabled:opacity-50 disabled:cursor-not-allowed text-sm sm:text-base"
                  >
                    {isSubmitting ? 'Sending...' : 'Send Message'}
                  </button>
                </form>
              </div>
              
              {/* Quick Contact */}
              <div className="space-y-8">
                <div>
                  <h3 className="text-2xl font-bold text-gray-800 mb-6">Quick Contact</h3>
                  <div className="space-y-4">
                    {obfuscatedContact.whatsapp && (
                      <a
                        href={obfuscatedContact.whatsapp}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="flex items-center space-x-3 p-4 bg-green-50 rounded-lg hover:bg-green-100 transition-colors"
                      >
                      <Icon name="MessageCircle" size={24} className="text-green-600" />
                      <div>
                        <div className="font-semibold text-green-800">WhatsApp Chat</div>
                        <div className="text-sm text-green-600">Get instant response</div>
                      </div>
                    </a>
                    )}
                    
                    <a
                      href="https://cal.com/octalchip/30min"
                      target="_blank"
                      rel="noopener noreferrer"
                      className="flex items-center space-x-3 p-4 bg-blue-50 rounded-lg hover:bg-blue-100 transition-colors"
                    >
                      <Icon name="Calendar" size={24} className="text-blue-600" />
                      <div>
                        <div className="font-semibold text-blue-800">Book a Meeting</div>
                        <div className="text-sm text-blue-600">30-minute consultation</div>
                      </div>
                    </a>
                  </div>
                </div>
                
                <div>
                  <h3 className="text-2xl font-bold text-gray-800 mb-6">Follow Us</h3>
                  <div className="flex space-x-4">
                    {socialLinks.map((social, index) => (
                      <a
                        key={index}
                        href={social.url}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="w-12 h-12 bg-[#00d690]/10 rounded-full flex items-center justify-center hover:bg-[#00d690] hover:text-white transition-colors"
                      >
                        <Icon name={social.icon} size={24} />
                      </a>
                    ))}
                  </div>
                </div>
                
                <div className="bg-gray-50 p-6 rounded-lg">
                  <h4 className="text-lg font-bold text-gray-800 mb-4">Response Time</h4>
                  <div className="space-y-2 text-sm text-gray-600">
                    <div className="flex justify-between">
                      <span>Email:</span>
                      <span className="font-medium">Within 24 hours</span>
                    </div>
                    <div className="flex justify-between">
                      <span>WhatsApp:</span>
                      <span className="font-medium">Within 2 hours</span>
                    </div>
                    <div className="flex justify-between">
                      <span>Phone:</span>
                      <span className="font-medium">Immediate</span>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* FAQ Section */}
      <FAQ 
        faqs={contactFAQs}
        title="Contact FAQs"
        description="Common questions about contacting us, business hours, consultations, and working with OctalChip."
      />

      <Footer />
    </div>
  );
};

export default ContactUs; 